import React from "react";
import { formatEventTimeRange } from "../../../utils/eventTimeDisplay";
import { getEventUrl } from "../../../utils/utils";

type EventListCardProps = {
  eventName: string;
  bannerImg: string;
  eventStatus: string;
  eventDate: string;
  totalTicket: number;
  soldTicket: number;
  eventId: number;
  eventData: any;
  eventShortDescription: string;
  eventTime: any;
  onDelete?: (eventId: number) => void;
};

/** Status badge colors for dark glass theme */
const getStatusStyling = (status: string) => {
  switch (status) {
    case "Draft":
      return {
        backgroundColor: "rgba(245, 158, 11, 0.15)",
        borderColor: "rgba(245, 158, 11, 0.45)",
        color: "#fbbf24",
        icon: "fa-solid fa-edit",
      };
    case "Live":
      return {
        backgroundColor: "rgba(16, 185, 129, 0.18)",
        borderColor: "rgba(16, 185, 129, 0.45)",
        color: "#34d399",
        icon: "fa-solid fa-circle-check",
      };
    default:
      return {
        backgroundColor: "rgba(255, 255, 255, 0.06)",
        borderColor: "rgba(255, 255, 255, 0.12)",
        color: "rgba(255, 255, 255, 0.7)",
        icon: "fa-solid fa-circle",
      };
  }
};

/**
 * Horizontal list card used on organizer event lists.
 * Single glass surface — no nested Bootstrap cards —
 * so status + date stay on one even meta row.
 */
const EventListCard = (props: EventListCardProps) => {
  const {
    eventName,
    eventShortDescription,
    bannerImg,
    eventStatus,
    eventDate,
    eventTime,
  } = props;
  const styling = getStatusStyling(eventStatus);

  const handleCardClick = () => {
    const url = getEventUrl(props.eventData?.slugId || "");
    if (url) {
      window.open(url, "_blank");
    }
  };

  const formatEventDate = (dateStr: string) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const date = new Date(year, month, day);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
    }
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const getFormattedDateTime = () => {
    const dateStr = formatEventDate(eventDate);
    const timeRange = formatEventTimeRange({
      isSameDayEvent: props.eventData?.isSameDayEvent,
      eventTime: eventTime || props.eventData?.eventTime,
      endTime: props.eventData?.endTime,
    });
    return timeRange ? `${dateStr}, ${timeRange}` : dateStr;
  };

  return (
    <div
      className="main-card ex-list-card"
      style={{ cursor: "pointer" }}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      <div className="ex-list-card-inner">
        <div className="card-event-img">
          <img src={bannerImg} alt={eventName} />
        </div>

        <div className="ex-list-card-body">
          <div className="ex-list-card-top">
            <div className="ex-list-card-copy">
              <h5>{eventName}</h5>
              {eventShortDescription ? (
                <p className="ex-list-card-desc">{eventShortDescription}</p>
              ) : null}
            </div>
          </div>

          {/* One aligned meta row — no overlapping icon-boxes */}
          <div className="ex-list-card-meta">
            <span
              className="ex-status-badge"
              style={{
                backgroundColor: styling.backgroundColor,
                color: styling.color,
                border: `1px solid ${styling.borderColor}`,
              }}
            >
              <i className={styling.icon}></i>
              {eventStatus}
            </span>
            <span className="ex-meta-date">
              <i className="fa-solid fa-calendar-days" aria-hidden="true"></i>
              <span>
                <em>Starts on</em>
                {getFormattedDateTime()}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
export default EventListCard;
